import { ActivatedChallengeLinkSet, ChallengeActivity, ChallengeLocationSummary, ChallengeReward, ChallengeType, CreateChallengeCheckin, GeoLocationCheckIn, GetPointResponse, IntegrationException, LatLng, LoginOrigin, ReceiptCheckInDetails, TranslationPhrases } from '../types';
import { GoogleTagEvent, logWithGoogleTag } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import challengeData, { challengeLocationsDisplayName } from '../helpers/state/challenge-data';
import { customElement, property, query, state } from 'lit/decorators';
import { detectMobileViewport, isInAppWebview, storageBlocked } from '../helpers/device';
import { getFromLocalStorage, isDefined, removeLocalStorage, setLocalStorage, undefinedIfEmpty } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { FormCheckbox } from './theme/form-checkbox';
import { ImageUploadComponent } from './image-upload-component';
import QrScanner from 'qr-scanner';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { add_challenge_checkin } from '../helpers/api/challenge-api';
import { getMainPage } from '../helpers/page-name';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('checkin-challenge-component')
export class CheckinChallengeComponent extends LitElement {
    static amplitude_callsite: string = 'Check In Challenge Modal';

    @property({ type: Boolean })
    open: boolean = false;

    @property({ type: Object })
    pendingCheckinPoint?: GetPointResponse;

    @property({ type: Object })
    pendingCheckinClue?: ChallengeLocationSummary;

    @property({ type: Object })
    pendingCheckinActivity?: ChallengeActivity;

    @property({ type: Boolean })
    showEmailConfirm: boolean = false;

    @property({ type: Boolean })
    isInMap: boolean = false;

    @query('#photo_feed')
    photoFeedApproval?: FormCheckbox;

    @query('#submission_image')
    submissionUploadElement!: ImageUploadComponent;



    @query('#code')
    codeElement!: HTMLInputElement;

    @query('#challengeAgreeement')
    challengeTermsElement!: FormCheckbox;

    @query('#submission_text')
    submissionTextElement!: HTMLInputElement;

    @query('#video-source')
    videoElement!: HTMLVideoElement;

    @state()
    private translatedLabels: TranslationPhrases = {};

    // Loading strings are props, so they can't use <translated-span>.
    private get labelPhrases(): TranslationPhrases {
        return {
            scanningQrCode: 'Scanning QR Code',
            processingCheckIn: 'Processing Check In',
            enterCode: 'Enter Location Confirmation Code',
        };
    }

    private async translateLabels() {
        this.translatedLabels = await translationData.translateFields(this.labelPhrases);
    }

    @state()
    userName: string = '';

    @state()
    title: string = '';

    @state()
    step: string = '';

    @state()
    emailAddress?: string;

    @state()
    oneTimeCode?: string;

    @state()
    hasCheckinChallenge?: boolean = false;

    @state()
    challengeLocation?: ChallengeLocationSummary;

    @state()
    pendingActivateChallenge: boolean = false;

    @state()
    storageTipDismissed: boolean = false;

    get showStorageTip(): boolean {
        return !this.storageTipDismissed && (isInAppWebview() || storageBlocked());
    }

    renderStorageTip() {
        if (!this.showStorageTip) {
            return '';
        }
        const message = isInAppWebview()
            ? 'Opened from another app? Tap ••• and choose “Open in browser” so you stay signed in.'
            : 'Your browser isn’t saving your sign-in — add this to your home screen to stay signed in.';
        return html`
            <p class="description">
                ${message}
                <button type="button" style="background:none;border:none;padding:0;text-decoration:underline;cursor:pointer;color:inherit;font:inherit;" @click="${() => { this.storageTipDismissed = true; }}">Dismiss</button>
            </p>
        `;
    }

    @state()
    showCriteria: boolean = false;

    @state()
    activeChallengeReward?: ChallengeReward;

    @state()
    uploadedImages: string[] = [];

    @state()
    receiptTotalSpend: string = '';

    @state()
    beforeAwards: ChallengeReward[] = [];

    @state()
    isProcessing: boolean = false;

    @state()
    checkInError?: string;

    qrScanner?: QrScanner;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountInfo = new StateController(this, accountInfo);
    private readonly bindChallengeState = new StateController(this, challengeData);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('open')) {
            await this.startup();
        }

        // if this.title is changed, dispatch event to update modal title
        if (changedProperties.has('title')) {
            this.dispatchEvent(new CustomEvent('on-update-challenge-modal-title', {
                detail: {
                    title: this.title,
                }
            }));
        }
    }

    async firstUpdated() {
        translationData.onLanguageChanged(() => {
            void this.translateLabels();
        });
        void this.translateLabels();
        await this.startup();
    }

    get hasPendingLocationCheckin() {
        return isDefined(this.pendingCheckinPoint) || isDefined(this.pendingCheckinClue);
    }

    private async startup() {
        this.pendingActivateChallenge = false;
        this.emailAddress = '';
        this.oneTimeCode = '';
        this.challengeLocation = undefined;
        this.checkInError = undefined;
        this.hasCheckinChallenge = isDefined(topicData.challengeHappeningNow);
        this.beforeAwards = challengeData.activated_challenge?.earned_rewards ?? [];

        if ((!accountInfo.isAuthenticated && !challengeData.activatedChallengeKey) && this.showEmailConfirm) {
            const signin_modal_status = getFromLocalStorage('signin_modal');
            if (isDefined(signin_modal_status)) {
                const status = JSON.parse(signin_modal_status);
                this.emailAddress = status.email ?? '';
                this.hasCheckinChallenge = status.hasCheckinChallenge ?? false;
                this.pendingActivateChallenge = status.pendingActivateChallenge ?? false;

                if (this.hasCheckinChallenge === true && this.hasPendingLocationCheckin) {
                    this.challengeLocation = challengeData.challengeLocations.find(
                        (l) => l.point_info?._id.$oid === this.pendingCheckinPoint?._id.$oid || l.challenge_location_info?._id?.$oid === this.pendingCheckinClue?.challenge_location_info?._id?.$oid
                    );
                }

                this.title = 'Confirm Email';
                this.step = 'signin_confirm';
            }
        } else {
            if (challengeData.challenge) {
                if (this.hasPendingLocationCheckin) {
                    this.challengeLocation = challengeData.challengeLocations.find(
                        (l) => (isDefined(l.point_info) && l.point_info?._id.$oid === this.pendingCheckinPoint?._id.$oid) || l.challenge_location_info?._id?.$oid === this.pendingCheckinClue?.challenge_location_info?._id?.$oid
                    );
                    this.hasCheckinChallenge = isDefined(this.challengeLocation);
                }
            };

            if (accountInfo.isAuthenticated || challengeData.activated_challenge) {
                if (isDefined(challengeData.activated_challenge)) {
                    if (this.hasPendingLocationCheckin && this.challengeLocation?.geo_location_eligible === true) {
                        this.title = challengeData.challenge?.name ?? '';
                        this.step = 'geolocation_checkin';
                    } else if (this.hasPendingLocationCheckin) {
                        this.title = challengeData.challenge?.name ?? '';
                        this.step = 'checkin_confirm';
                    } else if (this.pendingCheckinActivity) {
                        this.title = 'Activity Check In';
                        this.step = 'activity_checkin';
                    } else {
                        // eslint-disable-next-line quotes
                        this.title = "Check In";
                        this.step = 'general_checkin';
                    }
                } else {
                    if (this.hasPendingLocationCheckin) {
                        this.title = 'This map has a Challenge!';
                        this.step = 'has_challenge'; }
                    else {
                        this.title = challengeData.challenge?.name ?? '';
                        this.step = 'signin_terms';
                    }
                }
            } else {
                this.emailAddress = '';
                if (this.hasPendingLocationCheckin) {
                    if (this.hasCheckinChallenge) {
                        this.title = 'This Map has a Challenge!';
                        this.step = 'has_challenge';
                    } else {
                        this.title = 'Sign In';
                        this.step = 'signin_email';
                    }
                } else {
                    this.title = challengeData.challenge?.name ?? '';
                    this.step = 'signin_terms';
                }
            }
        }

        this.maybeWarnUnreachableCheckin();
    }

    // Diagnostic: when the check-in drawer opens but the resolved step has no
    // actionable UI to render (no methods enabled, honor-system mis-config, etc.)
    // surface a toast so we can debug field reports of "I clicked check in and
    // nothing happened."
    private maybeWarnUnreachableCheckin() {
        const c = challengeData.challenge;
        if (!c) return;

        // Only general_checkin can render truly empty. checkin_confirm always
        // renders either the "Confirm Check In" button (honor / auto-blank /
        // code) or the "Having trouble? Submit a photo for approval" link
        // (verify on), so it never needs a warning.
        if (this.step !== 'general_checkin') return;

        const hasUI =
            (c.should_verify_check_ins && c.enable_qr_checkin) ||
            (c.should_verify_check_ins && c.enable_code_checkin) ||
            c.enable_photo_checkin;

        if (!hasUI) {
            getMainPage().errorToastElement.showToast(
                `Check-in couldn't open — no methods are enabled on this challenge. (step=${this.step} verify=${String(c.should_verify_check_ins)} code=${String(c.enable_code_checkin)} qr=${String(c.enable_qr_checkin)} photo=${String(c.enable_photo_checkin)} receipt=${String(c.enable_receipt_checkin)} geo=${String(c.geofence_radius_m)})`
            );
        }
    }

    onChangeEmailAddress(e: any) {
        this.emailAddress = (e.target as HTMLInputElement).value;
    }

    onChangeOneTimeCode(e: any) {
        this.oneTimeCode = (e.target as HTMLInputElement).value;
    }

    onChangeUserName(e: any) {
        this.userName = (e.target as HTMLInputElement).value;
    }

    handleQRCode(result: QrScanner.ScanResult) {
        if (result?.data) {
            let key = result.data;

            // Special case where the key is in a URL.
            if (key.startsWith('http')) {
                const url = new URL(key);
                key = url.searchParams.get('challenge_location_qr_code_key') ?? '';
            }

            // Validate the parsed data
            if (isDefined(key)) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.handleSubmitQrCodeCheckIn(key);
            } else {
                getMainPage().errorToastElement.showToast('Invalid QR code');
            }

            // Stop and destroy the QR scanner
            this.qrScanner?.stop();
            this.qrScanner?.destroy();
            this.qrScanner = undefined;
        }
    }

    async scanQRCode() {
        this.step = 'qr_code';
        this.title = 'Scan QR Code';

        // if video element is not available, wait for it to be available
        if (!isDefined(this.videoElement)) {
            await new Promise<void>((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 1000);
            });
        }

        // Ensure the video element is visible
        if (isDefined(this.videoElement)) {
            this.videoElement.style.display = 'block';
            this.videoElement.style.visibility = 'visible';
        } else {
            getMainPage().errorToastElement.showToast('Video element is not available. Please try again.');
            return;
        }

        this.qrScanner = new QrScanner(
            this.videoElement,
            result => this.handleQRCode(result),
            {
                highlightScanRegion: true,
                highlightCodeOutline: true,
                overlay: undefined,
                returnDetailedScanResult: true,
            }
        );

        try {
            await this.qrScanner.start();
        } catch (error) {
            getMainPage().errorToastElement.showToast('Failed to start QR scanner. Please try again or check in manually.');
        }

        // put video element in the shadow dom modal__content

        const element = this.shadowRoot?.querySelector('.modal__content_video');
        if (element) {
            element.appendChild(this.qrScanner.$video);
            // set visibility of video element to visible
            this.qrScanner.$video.style.visibility = 'visible';
            this.qrScanner.$video.style.opacity = '1';
            this.qrScanner.$video.style.height = '150px';
            this.qrScanner.$video.style.width = '100%';
        }
    }

    async handleGoToChallenge() {
        if (accountInfo.isAuthenticated) {
            let link_set: ActivatedChallengeLinkSet | undefined;
            if (!challengeData.activatedChallengeId && isDefined(challengeData.challenge) && topicData.topic) {
                const new_participant = await challengeData.addActivatedChallenge(CheckinChallengeComponent.amplitude_callsite, accountInfo.email, accountInfo.phoneNumber, accountInfo.fullName);
                link_set = new_participant.link_set;
            }

            await topicData.mapComponent?.onActivateChallenge(link_set);

            if (this.hasPendingLocationCheckin && this.hasCheckinChallenge === true) {
                this.title = challengeData.challenge?.name ?? '';
                this.step = 'checkin_confirm';
            } else {
                this.dispatchEvent(
                    new CustomEvent('on-close')
                );
            }

        } else {
            this.pendingActivateChallenge = true;
            this.title = challengeData.challenge?.name ?? '';
            this.step = 'signin_terms';
        }
    }

    async handleGoToChallengeDetails() {
        this.pendingActivateChallenge = true;
        this.title = challengeData.challenge?.name ?? '';
        this.step = 'signin_terms';
    }


    async handleMarkAsVisited() {
        if (accountInfo.isAuthenticated) {
            if (topicData.topic && this.pendingCheckinPoint) {
                await accountInfo.createStandardCheckin(topicData.topic._id, this.pendingCheckinPoint.location);
                await pointCache.updateCheckIns(this.pendingCheckinPoint);
                logWithGoogleTag('Generic Check In', GoogleTagEvent.USER_CHECKED_IN, {
                    topic_id: topicData.topic?._id?.$oid,
                    point_id: this.pendingCheckinPoint._id.$oid,
                    organization_id: topicData.topic?.organization_id?.$oid,
                    challenge_name: challengeData.challenge?.name ?? '',
                });
            }
        } else {
            this.pendingActivateChallenge = false;
            this.hasCheckinChallenge = false;
            this.title = 'Sign In';
            this.step = 'signin_email';
        }
    }

    handleClickSignin() {
        this.pendingActivateChallenge = true;
        this.title = 'Sign In';
        this.step = 'signin_email';
    }

    handleOpenPhotoSubmission() {
        this.step = 'location_photo_submission';
        this.title = 'Photo Submission';
    }

    handleOpenReceiptSubmission() {
        this.step = 'location_receipt_submission';
        this.title = 'Receipt Submission';
    }

    private async handleSendVerificationCode() {
        const email = (this.emailAddress ?? '').trim().toLowerCase();

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(CheckinChallengeComponent.amplitude_callsite, email, LoginOrigin.Challenge, challengeData.challenge?.name, topicData.topic?.header_logo);
            const status = {
                email,
                pendingActivateChallenge: this.pendingActivateChallenge,
                hasCheckinChallenge: this.hasCheckinChallenge,
                pendingCheckinPointId: isDefined(this.pendingCheckinPoint) ? this.pendingCheckinPoint?._id.$oid : undefined,
                pendingCheckinClueId: isDefined(this.pendingCheckinClue) ? this.pendingCheckinClue?.challenge_location_info?._id?.$oid : undefined
            };

            setLocalStorage('signin_modal', JSON.stringify(status));

            this.title = 'Confirm Email';
            this.step = 'signin_confirm';
            this.requestUpdate();
        }, 'Oops! That doesn’t look like a valid email address.  Try again.');
    }

    private handleTryNewEmail() {
        removeLocalStorage('signin_modal');
        this.emailAddress = '';
        this.title = 'Sign In';
        this.step = 'signin_email';
    }

    private async handleSubmitOneTimeCode() {
        removeLocalStorage('signin_modal');

        const email = (this.emailAddress ?? '').trim().toLowerCase();
        const oneTimeCode = this.oneTimeCode ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            const origin = this.hasCheckinChallenge === true ? LoginOrigin.Challenge : LoginOrigin.Checkin;
            await upgradeToken(
                CheckinChallengeComponent.amplitude_callsite,
                email,
                oneTimeCode,
                undefined,
                origin
            );

            await this.postUpgradeUpdates();

            if (this.userName) {
                this.dispatchEvent(
                    new CustomEvent('on-update-username', {
                        detail: {
                            userName: this.userName,
                        }
                    })
                );
                accountInfo.fullName = this.userName;
            };


            if (this.pendingActivateChallenge && isDefined(challengeData.challenge) && isDefined(challengeData.hasActivatedChallenge) && topicData.topic) {
                await challengeData.addActivatedChallenge(CheckinChallengeComponent.amplitude_callsite, accountInfo.email, accountInfo.phoneNumber, accountInfo.fullName);

                await topicData.mapComponent?.onActivateChallenge();
            }

            if (this.hasPendingLocationCheckin) {
                if (this.hasCheckinChallenge === true) {
                    this.title = challengeData.challenge?.name ?? '';
                    this.step = 'checkin_confirm';
                } else {
                    if (this.pendingCheckinPoint) {
                        await pointCache.updateCheckIns(this.pendingCheckinPoint);
                    }
                }
            }
            if (topicData.mapComponent) {
                await topicData.mapComponent?.firstUpdated();
                await topicData.mapComponent?.unlockMap();
            }
        }, 'Hmm... That code isn’t right. Try a resending the code or entering a different email address.');
    }

    private async postUpgradeUpdates() {
        // Instantiate account cache
        await accountInfo.instantiateAccountData();

        // Close signin window
        if (window.location.hash.startsWith('#login')) {
            window.location.assign('/#home');
        } else {
            this.dispatchEvent(new CustomEvent('close'));
        }
    }

    // The location match in startup() can miss (stops pointing at re-created point
    // ids, or the locations list not loaded yet when the modal opened). Retry the
    // match at submit time and never send an empty location id — the server 400s it.
    private resolveChallengeLocationId(): string | undefined {
        const currentId = undefinedIfEmpty(this.challengeLocation?.challenge_location_info?._id?.$oid);
        if (currentId) {
            return currentId;
        }
        this.challengeLocation = challengeData.challengeLocations.find(
            (l) => (isDefined(l.point_info) && l.point_info?._id.$oid === this.pendingCheckinPoint?._id.$oid) || l.challenge_location_info?._id?.$oid === this.pendingCheckinClue?.challenge_location_info?._id?.$oid
        );
        return undefinedIfEmpty(this.challengeLocation?.challenge_location_info?._id?.$oid);
    }

    private showUnmatchedLocationToast() {
        getMainPage().errorToastElement.showToast('Hmm — we couldn’t connect this spot to the challenge. Close this window and tap Check In again. If it keeps happening, let the organizer know.');
    }

    private async handlePhotoCheckIn() {
        this.isProcessing = true;
        try {
            const challengeLocationId = this.resolveChallengeLocationId();
            if (!challengeLocationId) {
                this.showUnmatchedLocationToast();
                return;
            }

            const images = await this.submissionUploadElement?.uploadPendingImages() ?? [];

            if (images.length === 0) {
                getMainPage().errorToastElement.showToast('Please upload at least one image.');
                return;
            }

            let canBeDisplayed = false;
            if (this.photoFeedApproval) {
                canBeDisplayed = this.photoFeedApproval.checked ?? false;
            }

            const photo_check_in = {
                PhotoCheckIn: {
                    challenge_location_id: challengeLocationId,
                    image_urls: images,
                    can_be_displayed: canBeDisplayed
                }
            };
            const CheckInData : CreateChallengeCheckin = {
                method: photo_check_in
            };

            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(CheckInData, challengeData.activatedChallengeId,topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin(this.pendingCheckinPoint, true);
            }
        } catch (e) {
            getMainPage().errorToastElement.showToast('OOPS - We couldn’t submit your check-in. Try again or Reach out to us.');
        } finally {
            this.isProcessing = false;
        }
    }

    private async handleReceiptCheckIn() {
        this.isProcessing = true;
        try {
            const challengeLocationId = this.resolveChallengeLocationId();
            if (!challengeLocationId) {
                this.showUnmatchedLocationToast();
                return;
            }

            const images = await this.submissionUploadElement?.uploadPendingImages() ?? [];

            if (images.length === 0) {
                getMainPage().errorToastElement.showToast('Please upload at least one image.');
                return;
            }

            const totalSpend = parseFloat(this.receiptTotalSpend);
            if (!this.receiptTotalSpend || isNaN(totalSpend) || totalSpend <= 0) {
                getMainPage().errorToastElement.showToast('Please enter a valid total spend amount greater than zero.');
                return;
            }

            const currency = challengeData.challenge?.receipt_settings?.currency ?? 'USD';

            const receipt_check_in = {
                ReceiptCheckIn: {
                    challenge_location_id: challengeLocationId,
                    total_spend_in_cents: Math.round(totalSpend * 100),
                    image_url: images[0],
                    currency
                }
            };
            const CheckInData : CreateChallengeCheckin = {
                method: receipt_check_in
            };

            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(CheckInData, challengeData.activatedChallengeId, topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin(this.pendingCheckinPoint, true);
            }
        } catch (e) {
            getMainPage().errorToastElement.showToast("OOPS - We couldn't submit your check-in. Try again or Reach out to us.");
        } finally {
            this.isProcessing = false;
        }
    }

    private async handleGeolocationCheckIn(challengeLocationId?: string) {
        if (!challengeLocationId) {
            return;
        }
        const userLocation = await new Promise<LatLng>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    getMainPage().errorToastElement.showToast('OOPS - We don’t have access to your location. Please enable location services or check in with another method.');
                    reject(error);
                }
            );
        });
        this.isProcessing = true;
        try {

            const geolocation_check_in: GeoLocationCheckIn = {
               GeoLocationCheckIn: {
                    challenge_location_id: challengeLocationId,
                    user_location: {lat: userLocation.lat, lng: userLocation.lng}
                }
            };
            const CheckInData : CreateChallengeCheckin = {
                method: geolocation_check_in,
            };

            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(CheckInData, challengeData.activatedChallengeId,topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin(this.pendingCheckinPoint);
            }
        } catch (e) {
            getMainPage().errorToastElement.showToast(`You are not within the required radius ${challengeData.challenge?.geofence_radius_m ?? ''} meters. Get closer to the location and try another method.`);
        }
        this.isProcessing = false;
    };

    async handleSubmitQrCodeCheckIn(key: string) {
        this.isProcessing = true;
        try {
            const qr_code_check_in = {
                QrCodeCheckIn : {
                    key
                }
            };
            const checkInData : CreateChallengeCheckin = {
                method: qr_code_check_in
            };

            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(checkInData, challengeData.activatedChallengeId,topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin(this.pendingCheckinPoint);
            }
        } catch (e) {
            getMainPage().errorToastElement.showToast('OOPS - That QR code did not work. Try again.');
        }
        this.isProcessing = false;
    }

    private async handleSubmitCheckIn(location?: ChallengeLocationSummary, code?: string) {
        this.isProcessing = true;
        this.checkInError = undefined;
        location = location ?? this.challengeLocation;
        code = (code ?? this.codeElement?.value ?? '').trim();

        try {
            let checkinbody;
            if (undefinedIfEmpty(location?.challenge_location_info?._id?.$oid)) {
                const location_check_in = {
                    LocationCheckIn: {
                        challenge_location_id: location?.challenge_location_info?._id?.$oid ?? '',
                        code,
                    }
                };
                const CheckInData : CreateChallengeCheckin = {
                    method: location_check_in
                };
                checkinbody = CheckInData;
            } else {
                const code_check_in = {
                    CodeCheckIn: {
                        code,
                    }
                };
                const CheckInData : CreateChallengeCheckin = {
                    method: code_check_in
                };
                checkinbody = CheckInData;
            }
            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(checkinbody, challengeData.activatedChallengeId,topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin(this.pendingCheckinPoint);
            }
        } catch (e) {
            this.checkInError = 'That code isn’t right. Double check it and try again.';
        }
        this.isProcessing = false;
    };

    private async handleSubmitCheckInActivity() {
        this.isProcessing = true;
        try {
            const images = await this.submissionUploadElement?.uploadPendingImages() ?? [];
            const submission_type = challengeData.challenge?.activities?.find((a) => a._id?.$oid === this.pendingCheckinActivity?._id?.$oid)?.submission_type ?? 'photos';

            if ((submission_type === 'photos' || submission_type === 'receipt') && images.length === 0) {
                getMainPage().errorToastElement.showToast('Please upload at least one image.');
                return;
            }

            if (submission_type === 'receipt') {
                const totalSpend = parseFloat(this.receiptTotalSpend);
                if (!this.receiptTotalSpend || isNaN(totalSpend) || totalSpend <= 0) {
                    getMainPage().errorToastElement.showToast('Please enter a valid total spend amount greater than zero.');
                    return;
                }
            }

            const receipt_info: ReceiptCheckInDetails | undefined = submission_type === 'receipt' ? {
                challenge_location_id: this.pendingCheckinActivity?._id?.$oid ?? '',
                total_spend_in_cents: Math.round((parseFloat(this.receiptTotalSpend) || 0) * 100),
                image_url: images[0],
                currency: challengeData.challenge?.receipt_settings?.currency ?? 'USD',
            } : undefined;

            const activity_check_in = {
                ActivityCheckIn: {
                    activity_id: this.pendingCheckinActivity?._id?.$oid ?? '',
                    submission: isDefined(this.submissionTextElement) ? [this.submissionTextElement.value] : images,
                    receipt_info,
                    submission_type,
                    can_be_displayed: this.photoFeedApproval?.checked ?? false,
                }
            };

            const CheckInData : CreateChallengeCheckin = {
                method: activity_check_in
            };

            if (challengeData.activatedChallengeId) {
                await add_challenge_checkin(CheckInData, challengeData.activatedChallengeId,topicData.activatedChallengeKey);
                await this.runUpdatesAfterSuccessfulCheckin();
            }
        } catch (e) {
            getMainPage().errorToastElement.showToast('OOPS - We couldn’t submit your activity. You may have already submitted this activity or we couldn’t process your submission. Try again.');
        } finally {
            this.isProcessing = false;
        }
    }

    private async runUpdatesAfterSuccessfulCheckin(point?: GetPointResponse, awaitingApproval = false) {
        getMainPage().errorToastElement.showToast(`${awaitingApproval ? 'Your check in is awaiting approval.' : 'Check In successful!'}`);
        const beforeRewards = this.beforeAwards;
        await challengeData.refreshChallenge();
        if (point) {
            await pointCache.updateCheckIns(point);
        }

        logWithGoogleTag('Challenge Check In', GoogleTagEvent.USER_CHECKED_IN, {
            topic_id: topicData.topic?._id?.$oid,
            point_id: point?._id.$oid,
            organization_id: topicData.topic?.organization_id?.$oid,
            challenge_name: challengeData.challenge?.name ?? '',
        });

        const afterRewards = challengeData.activated_challenge?.earned_rewards ?? [];

        const newRewards = afterRewards.filter((r) => !beforeRewards.some((c) => c._id?.$oid === r._id?.$oid));

        if (newRewards.length > 0 && topicData.mapComponent) {
            topicData.mapComponent.newRewards = newRewards;
            await topicData.mapComponent.openAwardAnnouncementModal();
        }

        await challengeData.refreshCheckins();

        this.dispatchEvent(new CustomEvent('close-after-checkin', {
            detail: {
                newAwards: newRewards,
                location: challengeData.challengeLocations.find((l) => l.point_info?._id.$oid === point?._id.$oid || l.challenge_location_info?._id?.$oid === this.pendingCheckinClue?.challenge_location_info?._id?.$oid),
            }
        }));

        this.requestUpdate();
    }

    private renderCheckInError() {
        if (!this.checkInError) {
            return '';
        }
        return html`<p class="checkin-error">${this.checkInError}</p>`;
    }

    private openShowCriteria(e: CustomEvent){
        this.showCriteria = true;
        this.activeChallengeReward = e.detail.data;
    }

    get location_name() {
        return challengeData.isType(ChallengeType.Passport) || challengeData.isType(ChallengeType.Guide) ? this.pendingCheckinPoint?.name ?? this.pendingCheckinPoint?.location.search ?? '' : this.challengeLocation?.challenge_location_info?.clue_details?.clue ?? 'Anonymous Location';
    }

    get canGeoFenceCheckIn() {
        return challengeData.challenge?.should_verify_check_ins === true && isDefined(challengeData.challenge?.geofence_radius_m) && this.challengeLocation?.challenge_location_info?.can_geofence_checkin === true;
    }

    get canAutoCheckInBecauseCodeIsBlank() {
        return this.challengeLocation?.challenge_location_info?.check_in_code === '' || this.challengeLocation?.challenge_location_info?.check_in_code === 'NONE';
    }

    private renderByStep() {
        switch (this.step) {
            case 'has_challenge':
                return html`
                    <p class="description">
                        Earn points by checking into places on this map!
                    </p>
                    <div class="actions-wrapper">
                        <form-button
                            raised
                            class="action-button failure-red"
                            @click="${this.handleGoToChallengeDetails}"
                        >
                            Go to Challenge
                        </form-button>
                        ${this.pendingCheckinPoint ? html`
                            <form-button
                                outlined
                                class="action-button"
                                @click="${this.handleMarkAsVisited}"
                            >
                                Just Mark as Visited
                            </form-button>`
                        : ''}
                    </div>
                `;
            case 'signin_terms':
                return html`
                    <registration-page is_embedded .isInMap=${this.isInMap}></registration-page>
                `;
            case 'signin_email':
                return html`
                    <div class="signin-wrapper">
                        ${this.renderStorageTip()}
                        <proxi-textfield
                            class="input__email-address"
                            id="email"
                            type="email"
                            label="Email Address"
                            placeholder=""
                            outlined
                            value="${this.emailAddress ?? ''}"
                            @change="${this.onChangeEmailAddress}"
                        >
                        </proxi-textfield>
                        <form-button
                            raised
                            class="action-button action-button__send-code"
                            ?disabled="${!this.emailAddress}"
                            @click="${this.handleSendVerificationCode}"
                        >
                            Send Verification Code
                        </form-button>
                        <p>
                            By signing in, you can keep track of where you've visited, your progress, and bookmark maps for later.
                        </p>
                    </div>
                `;
            case 'signin_confirm':
                return html`
                    <div class="signin-wrapper">
                        ${this.renderStorageTip()}
                        <p>
                            Enter the code sent to <b>${this.emailAddress ?? ''}</b>.
                        </p>
                        <proxi-textfield
                            class="input__confirm-code"
                            id="code"
                            type="string"
                            label="Access Code"
                            placeholder="000000"
                            outlined
                            value="${this.oneTimeCode ?? ''}"
                            @change="${this.onChangeOneTimeCode}"
                        >
                        </proxi-textfield>
                        <p class="description">Didn't get the email? It can take a minute to arrive — and be sure to check your spam or junk folder.</p>
                        <proxi-textfield
                            class="input__user-name"
                            id="name"
                            type="string"
                            label="${this.hasCheckinChallenge === true ? 'Username' : 'Name'}"
                            outlined
                            placeholder="Your Name"
                            value="${this.userName ?? ''}"
                            @change="${this.onChangeUserName}"
                        >
                        </proxi-textfield>
                        ${this.hasCheckinChallenge === true ? html`<p class="description">Your username may be displayed on a public leaderboard.</p>` : ''}
                        <form-button
                            raised
                            class="action-button action-button__confirm"
                            ?disabled="${!this.oneTimeCode}"
                            @click="${this.handleSubmitOneTimeCode}"
                        >
                            ${this.hasCheckinChallenge === true
                                ? 'Confirm & Start Challenge'
                                : 'Confirm'
                            }
                        </form-button>

                        <div class="link-buttons">
                            <p class="link-btn" @click=${this.handleTryNewEmail}>
                                Try Another Email
                            </p>
                            <p class="link-btn" @click=${this.handleSendVerificationCode}>
                                Send a New Code
                            </p>
                        </div>
                    </div>
                `;
            case 'checkin_confirm':
                return html`
                    <div class="signin-wrapper checkin-confirm">
                        ${!detectMobileViewport() ? html`
                            <p>${undefinedIfEmpty(challengeData.challenge?.button_text) ?? 'Check In'}</p>
                        ` : ''}
                        <p class="location-name">
                            ${challengeData.isType(ChallengeType.Passport) || challengeData.isType(ChallengeType.Guide) ? html`
                                ${this.location_name}</p>
                            ` : challengeData.isType(ChallengeType.Hunt) ? html`
                                ${challengeLocationsDisplayName(challengeData.challenge, false, 'title')}: ${this.challengeLocation?.challenge_location_info?.clue_details?.clue ?? 'Anonymous Location'}</p>
                            ` :''}
                        </p>
                        ${challengeData.challenge?.should_verify_check_ins === false || this.canAutoCheckInBecauseCodeIsBlank ? '' : html`
                            ${challengeData.challenge?.enable_qr_checkin === true ? html`
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${this.scanQRCode}"
                                    icon="qr_code"
                                    ?fullWidth="${true}"
                                >
                                    <translated-span>Scan QR Code to Check In</translated-span>
                                </form-button>
                            ` : ''}
                            ${challengeData.challenge?.enable_photo_checkin === true ? html`
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${this.handleOpenPhotoSubmission}"
                                    icon="photo_camera"
                                    ?fullWidth="${true}"
                                >
                                    <translated-span>Submit Photo for Approval</translated-span>
                                </form-button>
                            ` : ''}
                            ${challengeData.challenge?.enable_receipt_checkin === true ? html`
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${this.handleOpenReceiptSubmission}"
                                    icon="receipt"
                                    ?fullWidth="${true}"
                                >
                                    <translated-span>Submit Receipt for Approval</translated-span>
                                </form-button>
                            ` : ''}

                            ${this.canGeoFenceCheckIn ? html`
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${async () => await this.handleGeolocationCheckIn(this.challengeLocation?.challenge_location_info?._id?.$oid)}"
                                    ?fullWidth="${true}"
                                    icon="location_on"
                                >
                                    <translated-span>Try Geolocation to Check In</translated-span>
                                </form-button>
                            ` : ''}
                        `}

                        ${challengeData.challenge?.should_verify_check_ins === true && challengeData.challenge?.enable_code_checkin && !this.canAutoCheckInBecauseCodeIsBlank ? html`
                            ${this.canGeoFenceCheckIn || challengeData.challenge?.enable_qr_checkin || challengeData.challenge?.enable_photo_checkin || challengeData.challenge?.enable_receipt_checkin ? html`
                                <div class="checkin-or">
                                    <hr/>
                                </div>`
                            : ''}
                            <proxi-textfield
                                id="code"
                                style="width: 99%;"
                                outlined
                                type="string"
                                label=""
                                maxLength="25"
                                placeholder="${this.translatedLabels.enterCode ?? 'Enter Location Confirmation Code'}"
                            >
                            </proxi-textfield>
                            ${this.renderCheckInError()}`
                        : ''}
                        ${challengeData.challenge?.should_verify_check_ins !== true || (challengeData.challenge?.enable_code_checkin || this.canAutoCheckInBecauseCodeIsBlank) ? html`
                            <form-button
                                raised
                                size="lg"
                                ?fullWidth=${true}
                                class="action-button "
                                @click="${async () => await this.handleSubmitCheckIn()}"
                            >
                                ${challengeData.challenge?.button_text ?? 'Confirm Check In'}
                            </form-button>
                        ` : ''}
                        ${!(challengeData.challenge?.should_verify_check_ins === true && challengeData.challenge?.enable_photo_checkin) ? '' : html`
                            <div class="extra-info">
                                <p class="description_info">
                                    ${topicData.hasExceptions === IntegrationException.HighStreetSafari ? html`
                                        <span style="cursor:pointer; text-decoration: underline;" @click=${this.handleOpenPhotoSubmission}>
                                        <translated-span>Having trouble?</translated-span>
                                        </span>
                                    ` : html`
                                        <translated-span>Having trouble?</translated-span>
                                        <span style="cursor:pointer; text-decoration: underline;" @click=${this.handleOpenPhotoSubmission}>
                                        <translated-span>Submit a photo for approval.</translated-span>
                                        </span>
                                    `}
                                </p>
                            </div>
                             ${this.challengeLocation?.geo_location_eligible === false && isDefined(challengeData.challenge?.geofence_radius_m) ? html`
                                <div class="extra-info">
                                    <p class="description_info">
                                        For Geolocation Issues:
                                        <span @click=${async () => await challengeData.refreshGeoEligibility()} style="cursor:pointer; text-decoration: underline;">Click here to re-request your location</span>.
                                        </p>
                                </div>`
                            : ''}
                        `}
                    </div>
                `;
            case 'geolocation_checkin':
                return html`
                    <div class="signin-wrapper">
                        <p><translated-span>Check In</translated-span></p>
                        <p class="location-name">${this.location_name}</p>
                        <p><translated-span>You are within the check-in radius!</translated-span></p>
                        <form-button
                            raised
                            class="action-button "
                            @click="${async () => await this.handleGeolocationCheckIn(this.challengeLocation?.challenge_location_info?._id?.$oid ?? '')}"
                        >
                            ${challengeData.challenge?.button_text ?? 'Confirm Check In'}
                        </form-button>
                    </div>
                `;
            case 'general_checkin':
                return html`
                    <div class="signin-wrapper checkin-confirm">
                        ${!detectMobileViewport() ? html`
                            <p>${undefinedIfEmpty(challengeData.challenge?.button_text) ?? 'Check In'} (General)</p>
                        ` : ''}
                        ${challengeData.challenge?.enable_qr_checkin === true && challengeData.challenge?.should_verify_check_ins ? html`
                            <form-button
                                variant="outline"
                                class="action-button"
                                @click="${this.scanQRCode}"
                                icon="qr_code"
                                ?fullWidth="${true}"
                            >
                                <translated-span>Scan QR Code to Check In</translated-span>
                            </form-button>
                        ` : ''}
                        ${challengeData.challenge?.should_verify_check_ins === true && challengeData.challenge?.enable_code_checkin ? html`
                            <div class="checkin-or">
                                <hr/>
                            </div>
                            <proxi-textfield
                                id="code"
                                outlined
                                style="width: 99%;"
                                type="string"
                                label=""
                                maxLength="25"
                                placeholder="${this.translatedLabels.enterCode ?? 'Enter Location Confirmation Code'}"
                            >
                            </proxi-textfield>
                            ${this.renderCheckInError()}
                            <form-button
                                raised
                                size="lg"
                                ?fullWidth=${true}
                                class="action-button "
                                @click="${async () => await this.handleSubmitCheckIn()}"
                            >
                                ${challengeData.challenge?.button_text ?? 'Confirm Check In'}
                            </form-button>
                        ` : ''}
                        ${challengeData.challenge?.enable_photo_checkin === true || this.canGeoFenceCheckIn ? html`
                            <div class="extra-info">
                                <p class="description_info">
                                    ${challengeData.challenge?.enable_photo_checkin === false ? html`
                                        <translated-span>Having trouble? Go to the location you're trying to check in at and tap Check In, then try to check in within the geofence.</translated-span>
                                    ` : isDefined(challengeData.challenge?.geofence_radius_m) ? html`
                                        <translated-span>Having trouble? Go to the location you're trying to check in at and tap Check In. Then upload a photo or try to check in within the geofence.</translated-span>
                                    ` : html`
                                        <translated-span>Having trouble? Go to the location you're trying to check in at and tap Check In. Then upload a photo.</translated-span>
                                    `}
                                </p>
                            </div>
                        ` : ''}
                    </div>
                `;
            case 'activity_checkin':
                return html`
                    <div class="signin-wrapper">
                        <p class="location-name"><b>${this.pendingCheckinActivity?.name ?? ''}</b></p>
                        <p>${this.pendingCheckinActivity?.description ?? ''}</p>
                    ${challengeData.challenge?.activities?.find((a) => a._id?.$oid === this.pendingCheckinActivity?._id?.$oid)?.submission_type === 'photos' ? html`
                            <p class="description_info">
                                ${challengeData.challenge?.photo_instructions ?? ''}
                            </p>
                            <image-upload-component
                                id="submission_image"
                                allowMultiple
                                .imageUrls="${this.uploadedImages}"
                            ></image-upload-component>
                            ${challengeData.challenge?.show_photo_feed ? html`
                                <div class="checkbox-wrapper">
                                    <form-checkbox
                                        id="photo_feed"
                                        name="allowInFeed"
                                        ?checked="${true}"
                                        >
                                    </form-checkbox>
                                    <span><translated-span>Show my photo in the public feed</translated-span></span>
                                </div>
                            ` : ''}
                        `: challengeData.challenge?.activities?.find((a) => a._id?.$oid === this.pendingCheckinActivity?._id?.$oid)?.submission_type === 'receipt' ? html`
                            <p class="description_info">
                                ${challengeData.challenge?.receipt_settings?.instructions ?? ''}
                            </p>
                            <image-upload-component
                                id="submission_image"
                                allowMultiple
                                .imageUrls="${this.uploadedImages}"
                            ></image-upload-component>
                            <proxi-textfield
                                id="receipt_total_spend"
                                style="width: 99%; margin-bottom: 16px;"
                                outlined
                                type="number"
                                label="Total Spend (${challengeData.challenge?.receipt_settings?.currency ?? 'USD'})"
                                placeholder="0.00"
                                .value="${this.receiptTotalSpend}"
                                @input="${(e: any) => {this.receiptTotalSpend = e.target.value;}}"
                            >
                            </proxi-textfield>
                        ` : html`
                            <proxi-textfield
                                id="submission_text"
                                label="Submission"
                                placeholder="Enter your submission here"
                                type="text"
                                outlined
                                required
                                .value=${challengeData.challenge?.activities?.find((a) => a._id?.$oid === this.pendingCheckinActivity?._id?.$oid)?.submission_type === 'text' ? 'text' : 'url'}
                            ></proxi-textfield>`
                        }
                        <form-button
                            raised
                            fullWidth
                            size="md"
                            class="action-button "
                            @click="${this.handleSubmitCheckInActivity}"
                        >
                            <translated-span>Submit for Approval</translated-span>
                        </form-button>
                    </div>
                `;
            case 'qr_code':
            return html`
                <video id="video-source" style="height: 100%; width: 100%;background-color: black;"></video>
                ${isDefined(this.qrScanner) ? html`
                    <loading-element
                        .loadingString=${this.translatedLabels.scanningQrCode ?? 'Scanning QR Code'}
                        .horizontal=${true}></loading-element>
                    <p><translated-span>Point the camera at the QR code to scan</translated-span></p>
                ` : ''}
                    <form-button
                        raised
                        class="action-button"
                        @click="${() => {
                            this.qrScanner?.stop();
                            this.qrScanner?.destroy();
                            this.qrScanner = undefined;
                            this.step = 'general_checkin';
                        }}"
                    >
                        <translated-span>Cancel</translated-span>
                    </form-button>

            `;
            case 'location_photo_submission':
                return html`
                    <div class="signin-wrapper">
                        <p><translated-span>Check In</translated-span></p>
                        <p class="location-name">${this.location_name}</p>
                        <p class="description_info">
                            ${topicData.hasExceptions === IntegrationException.HighStreetSafari
                                ? "Can't find the marker? Take a quick photo at this location and we'll unlock this stop for you."
                                : (challengeData.challenge?.photo_instructions ?? 'Take a photo of you at this location for the admin to approve this visit. Ensure that both you and the location are visible in the photo so that we can verify your visit.')}
                        </p>
                        <image-upload-component
                            id="submission_image"
                            allowMultiple
                            .imageUrls="${this.uploadedImages}"
                        ></image-upload-component>
                        ${challengeData.challenge?.show_photo_feed === true ? html`
                            <div class="checkbox-wrapper">
                                <form-checkbox
                                    id="photo_feed"
                                    name="allowInFeed"
                                    ?checked="${true}"
                                    >
                                </form-checkbox>
                                <span><translated-span>Show my photo in the public feed</translated-span></span>
                            </div>
                        ` : ''}
                        ${!this.isProcessing ? html`
                        <form-button
                            raised
                            class="action-button"
                            @click="${this.handlePhotoCheckIn}"
                        >
                            ${topicData.hasExceptions === IntegrationException.HighStreetSafari ? html`<translated-span>Submit image</translated-span>` : html`<translated-span>Submit Image for Approval</translated-span>`}
                        </form-button>
                        ` : ''}
                        ${topicData.hasExceptions === IntegrationException.HighStreetSafari ? '' : html`
                            <p class="description_info">
                                <translated-span>Submit Image for Approval to Check In at</translated-span> ${this.location_name}
                            </p>
                        `}
                    </div>
                    `;
            case 'location_receipt_submission':
                return html`
                    <div class="signin-wrapper">
                        <p><translated-span>Check In</translated-span></p>
                        <p class="location-name">${this.location_name}</p>
                        <p class="description_info">
                            ${challengeData.challenge?.receipt_settings?.instructions ?? 'Enter your total spend (before tax) at this location for admin approval.'}
                        </p>
                        <br>
                        <image-upload-component
                            id="submission_image"
                            allowMultiple
                            .imageUrls="${this.uploadedImages}"
                        ></image-upload-component>
                        <proxi-textfield
                            id="receipt_total_spend"
                            style="width: 99%; margin-bottom: 16px;"
                            outlined
                            type="number"
                            label="Total Spend (${challengeData.challenge?.receipt_settings?.currency ?? 'USD'})"
                            placeholder="0.00"
                            .value="${this.receiptTotalSpend}"
                            @input="${(e: any) => {this.receiptTotalSpend = e.target.value;}}"
                        >
                        </proxi-textfield>
                        ${!this.isProcessing ? html`
                        <form-button
                            raised
                            class="action-button"
                            @click="${this.handleReceiptCheckIn}"
                        >
                            <translated-span>Submit Receipt for Approval</translated-span>
                        </form-button>
                        ` : ''}
                        <p class="description_info">
                            <translated-span>Submit Receipt for Approval to Check In at</translated-span> ${this.location_name}
                        </p>
                    </div>
                    `;
            default:
                return '';
        }
    }

    render() {
        return html`
            <div class="modalContent">
                ${this.isProcessing ? html`<loading-element .loadingString=${this.translatedLabels.processingCheckIn ?? 'Processing Check In'}></loading-element>` : ''}
                <div class="modal__content ${this.step==='qr_code' ? 'modal__content_video' :''} ${this.step==='signin_terms' ? 'modal__content_passthrough' :''}">${this.renderByStep()}</div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --modal-width: 550px;
            }

            .modalContent {
                position: relative;
                padding: 0;
                max-height: 100%;
                height: auto;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .modal__header {
                width: 100%;
                padding: 0px 20px 5px 0px;
                border-bottom: solid 1px #efecf3;
                color: var(--theme-input-color);
                font-size: 18px;
                font-weight: 700;
                line-height: 1.3em;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 5px 0;
                width: 100%;
                box-sizing: border-box;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .modal__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .modal__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .modal__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .modal__content_video {
                padding: 5px 0;
                width: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction:column-reverse;
                align-items: center;
            }

            /* Registration step renders a registration-page that owns its
               own scroll container (.card-content__scrollable) plus a
               sticky footer. Suppress modal__content's scroll so we don't
               end up with two scrollbars side-by-side. */
            .modal__content_passthrough {
                overflow: visible;
                display: flex;
                flex-direction: column;
                flex: 1;
                min-height: 0;
                padding: 0;
            }

            .description_info {
                font-size: 12px;
                line-height: 12px;
                text-align: center;
            }

            .extra-info {
                background-color: var(--theme-border-color);
                border-radius: 8px;
                padding: 10px;
                margin-top: 10px;
                font-size: 0.75rem;
            }

            .actions-wrapper {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                gap: 5px;
                flex-wrap: wrap;
            }

            .column {
                flex-direction: column;
            }

            .failure-red {
                --mdc-theme-primary: var(--main-invalid-color);
            }


            .terms__desc-wrapper {
                text-align: left;
                overflow-y: auto;
                box-sizing: border-box;
                line-height: 130%;
                color: var(--theme-input-color);
                margin: 20px 0;
                flex: 1;
            }

            .terms__checkbox-wrapper {
                width: 100%;
                box-sizing: border-box;
                margin: 10px 0;
            }

            #challengeAgreeement {
                --form-checkbox-label-color: var(--theme-input-color);
            }

            p {
                text-align: left;
                margin: 5px 0;
            }

            .check_in_option {
                padding: 5px;
                border: 1px solid var(--main-primary-color);
                border-radius: 8px;
                margin: 10px 0;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }

            .description {
                width: 100%;
                color: var(--theme-input-color);
                margin-bottom: 1px;
            }

            .location-name {
                font-size: 18px;
                font-weight: 700;
            }

            .checkin-error {
                color: var(--main-invalid-color);
                font-size: 0.85rem;
                margin: 5px 0 0;
                text-align: center;
            }

            .signin-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: stretch;
                justify-content: flex-start;
            }

            .checkin-confirm {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
            }

            .flexContainer {
                display: flex;
            }

            .action-button {
                width: 100%;
                margin: 10px 0;
            }

            .justifySpaceBetween {
                justify-content: space-between;
            }

            .link-buttons {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin-top: 20px;
            }

            .link-btn {
                cursor: pointer;
                text-decoration: underline;
                line-height: 125%;
                color: var(--theme-input-color);
            }

            .email-address {
                color: var(--theme-input-color);
            }

            .checkin-or {
                display: flex;
                flex-direction: column;
                width: 100%;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }


            .geo-eligible {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                gap: 10px;
                overflow-x: scroll;
                width: 30vw;
                padding: 10px 0;
            }

            .action-button__send-code {
                width: 100%;
                max-width: 240px;
                margin: 20px 0;
            }

            .action-button__confirm {
                width: 100%;
                max-width: 250px;
                margin: 15px 0;
            }

            .input__email-address {
                width: 100%;
                max-width: 240px;
                margin-top: 15px;
                text-align: center;
            }

            .input__confirm-code,
            .input__user-name {
                width: 100%;
                max-width: 250px;
                margin: 5px 0;
                text-align: center;
            }

            hr {
                border: none;
                border-top: 1px double #333;
                color: var(--theme-border-color);
                overflow: visible;
                text-align: center;
                height: 5px;
                width: 100%;
            }

            hr::after {
                background: #fff;
                content: "OR";
                padding: 0 4px;
                position: relative;
                top: -8px;
                color: var(--main-primary-color);
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }

                .geo-eligible {
                    width:90vw;
                }
            }
        `;
    }
}
